import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import ReactDOM from "react-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Search, TrendingUpDown, CircleOff, Plus, Check, ChevronDown, Percent, RotateCcw } from "lucide-react";
import TabSelector from "../ui/TabSelector";
import Tab from "../ui/Tab";
import Label from "../Label";
import Input from "../Input";
import Chip from "../ui/Chip";
import Divider from "../ui/Divider";
import Button from "../Buttons/Button";
import TaplrTooltip from "../Tooltip";
import { Tooltip } from "@material-ui/core";

const ProjectionsDialog = ({ dropdownOpen, accountCategories, handleSetActiveProjection, activeProjection, mobileAlignment, ...props }) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);

  // Tabs
  const [selectedTab, setSelectedTab] = useState("prices");

  // Account categories
  const [allAccounts, setAllAccounts] = useState([]);
  const [accountSearchValue, setAccountSearchValue] = useState("");
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);

  // account dropdown logic
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownWidth, setDropdownWidth] = useState(0);
  const dropdownRef = useRef(null);
  const openDropdownButtonRef = useRef(null);

  const [currentProjection, setCurrentProjection] = useState(null);
  const [appliedProjection, setAppliedProjection] = useState(null); // The projection that is applied to the parent page content
  const [revenueCollapsed, setRevenueCollapsed] = useState(false);
  const [costsCollapsed, setCostsCollapsed] = useState(false);

  // create mobileAlignment class mobileAlignment_"prop"

  const mobileAlignmentClass = clsx({
    [classes[`${mobileAlignment}MobileAlignment`]]: mobileAlignment !== null,
  });

  useEffect(() => {
    let tempAccounts = null;
    if (accountCategories && accountCategories.length > 0) {
      // for every account in accountCategory.accounts, set isRevenue (get from category) and push to tempAccounts
      tempAccounts = accountCategories.map((category) => {
        return category.accounts.map((account) => {
          return {
            ...account,
            categoryName: category.name,
            categoryId: category.id,
            isRevenue: category.isRevenue,
            companyName: category.companyName,
          };
        });
      });
      // flatten the array
      tempAccounts = tempAccounts.flat();
      setAllAccounts(tempAccounts);
    } else {
      // if no account categories, set allAccounts to empty array
      setAllAccounts([]);
    }
  }, [accountCategories]);

  // AccountDropdown Logic
  useEffect(() => {
    if (accountDropdownOpen && openDropdownButtonRef.current) {
      const rect = openDropdownButtonRef.current.getBoundingClientRect();

      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4, // Adjust for scroll position
        left: rect.left + window.scrollX, // Adjust for horizontal scrolling
      });

      setDropdownWidth(rect.width);
    }
  }, [accountDropdownOpen]);

  useEffect(() => {
    const handleClickOutsideAccountDropdown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && accountDropdownOpen) {
        // check if target is not the button
        if (!openDropdownButtonRef.current.contains(event.target)) {
          setAccountDropdownOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutsideAccountDropdown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideAccountDropdown);
    };
  }, [accountDropdownOpen]);

  // Dropdown logic
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClickOutside = (event) => {
    if (buttonRef.current && !buttonRef.current.contains(event.target) && (!dropdownRef.current || !dropdownRef.current.contains(event.target))) {
      setIsOpen(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Tab selection logic
  const handleGraphTypeSelection = (value) => {
    setSelectedTab(value);
  };

  const handleAccountSelection = (account) => {
    let tempAccount = {
      percentage: 100,
      ...account,
    };
    let tempProjection = null;
    if (currentProjection) {
      tempProjection = { ...currentProjection };
    } else {
      tempProjection = { accounts: [] };
    }

    if (tempProjection.accounts.some((a) => a.id === tempAccount.id)) {
      // if account is already selected, remove it from the projection
      tempProjection.accounts = tempProjection.accounts.filter((acc) => acc.id !== tempAccount.id);
    } else {
      // if account is not selected, add it to the projection
      tempProjection.accounts.push(tempAccount);
    }
    setCurrentProjection(tempProjection);
  };

  // Selected indicator
  const SelectedIndicator = ({ isSelected }) => {
    // if isSelected, return a green circle with a checkmark inside. if not return a grey circle with a plus inside
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {isSelected ? (
          <div style={{ height: "20px", width: "20px", borderRadius: "50%", backgroundColor: "#5CC958", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Check size={16} color={"#FFFFFF"} />
          </div>
        ) : (
          <div style={{ height: "20px", width: "20px", borderRadius: "50%", backgroundColor: "#EDEDED", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Plus size={16} color={"#0C0E29"} />
          </div>
        )}
      </div>
    );
  };

  const toggleRevenueCollapsed = () => {
    setRevenueCollapsed((prev) => !prev);
  };

  const toggleCostsCollapsed = () => {
    setCostsCollapsed((prev) => !prev);
  };

  const setAccountProjectionPercentage = (accountId, value) => {
    let tempProjection = { ...currentProjection };
    let tempAccount = tempProjection.accounts.find((account) => account.id === accountId);
    if (tempAccount) {
      tempAccount.percentage = value;
    }
    setCurrentProjection(tempProjection);
  };

  const handleApplyProjection = () => {
    handleSetActiveProjection(JSON.parse(JSON.stringify(currentProjection))); // Use deep copy to avoid reference issues
    setAppliedProjection(JSON.parse(JSON.stringify(currentProjection))); // Use deep copy to avoid reference issues
  };

  const handleResetProjection = () => {
    // reset currentProjection and appliedProjection to null
    setCurrentProjection(null);
    setAppliedProjection(null);
    handleSetActiveProjection(null); // Reset the active projection in the parent component
  };

  const didAccountChange = (accountId) => {
    // Check if the accountId is in the appliedProjection and not in the currentProjection
    const accountInAppliedProjection = appliedProjection?.accounts?.find((account) => account.id === accountId);
    const accountInCurrentProjection = currentProjection?.accounts?.find((account) => account.id === accountId);

    if (!accountInAppliedProjection || !accountInCurrentProjection) {
      return false; // If either account is not found, return false
    }

    return accountInAppliedProjection.percentage !== accountInCurrentProjection.percentage;
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }} ref={buttonRef}>
      <button className={`${classes.button} ${isOpen ? classes.buttonOpen : ""}`} onClick={toggleDropdown}>
        Projections
        <TrendingUpDown size={16} color={"#0C0E29"} />
      </button>
      <div
        className={`${classes.dropdown} ${isOpen ? classes.dropdownOpen : classes.dropdownClosed} ${mobileAlignmentClass}`}
        // style={{ left: mobileAlignment === "right" ? "auto" : null, right: mobileAlignment === "right" ? 0 : null, transform: mobileAlignment !== null ? "translateX(0)" : null }}
      >
        <TabSelector value={selectedTab} onChange={handleGraphTypeSelection} style={{ backgroundColor: "#EDEDED", marginBottom: "16px" }}>
          <Tab value="events">Events</Tab>
          <Tab value="prices">Prices</Tab>
        </TabSelector>
        {/* Tab contents */}
        {/* Prices */}
        {selectedTab === "prices" && (
          <div className={classes.tabContent}>
            <div className={classes.addAccountContainer}>
              <Label>Add an Account</Label>
              <div ref={openDropdownButtonRef} className={`${classes.selectTriggerInput}`} onClick={() => setAccountDropdownOpen(true)}>
                <input
                  type="text"
                  style={{ width: "100%" }}
                  value={accountSearchValue}
                  onChange={(e) => setAccountSearchValue(e.target.value)}
                  onFocus={() => setAccountDropdownOpen(true)}
                  className={classes.selectTriggerInputField}
                  placeholder="Account Name"
                />
                <button
                  type="button"
                  className={classes.selectTriggerChevronButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAccountDropdownOpen(!accountDropdownOpen);
                  }}
                >
                  <ChevronDown className={`${classes.selectTriggerIcon} ${accountDropdownOpen ? classes.selectTriggerIconOpen : ""}`} />
                </button>
              </div>
              {accountDropdownOpen &&
                ReactDOM.createPortal(
                  <div ref={dropdownRef} className={classes.dropdownContainer} style={{ top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownWidth }}>
                    {allAccounts.filter((account) => account.name.toLowerCase().includes(accountSearchValue.toLowerCase())).length > 0 ? (
                      allAccounts
                        .filter((account) => account.name.toLowerCase().includes(accountSearchValue.toLowerCase()))
                        .map((account, index) => (
                          <button className={classes.dropdown__menuItem} onClick={() => handleAccountSelection(account)} key={"Type_" + index}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {account.name} - {account.companyName}
                              {account.isRevenue ? (
                                <div style={{ height: "6px", width: "6px", borderRadius: "50%", backgroundColor: "#5CC958" }} />
                              ) : (
                                <div style={{ height: "6px", width: "6px", borderRadius: "50%", backgroundColor: "#D84949" }} />
                              )}
                            </div>
                            {/* selected indicator. Check if account is included in currentProjection */}
                            <SelectedIndicator isSelected={currentProjection?.accounts?.some((a) => a.id === account.id)} />
                          </button>
                        ))
                    ) : (
                      <div className={classes.dropdown__emptyState}>
                        <CircleOff className={classes.emptyState__icon} />
                        <p className={classes.emptyState__text}>There are no accounts to select</p>
                      </div>
                    )}
                  </div>,
                  document.body
                )}
            </div>
            {/* Show selected accounts */}

            <div className={classes.currentProjectionContainer}>
              {/* Revenue */}
              {currentProjection?.accounts?.filter((account) => account.isRevenue).length > 0 && (
                <div className={classes.currentProjection__category}>
                  <div className={classes.currentProjection__categoryHeader} onClick={toggleRevenueCollapsed}>
                    <div className={classes.currentProjection__categoryTitleContainer}>
                      <Chip variant="success">Revenue</Chip>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <p className={classes.currentProjection__categoryCount}>
                        {currentProjection?.accounts?.filter((account) => account.isRevenue).length} Simulation
                        {currentProjection?.accounts?.filter((account) => account.isRevenue).length > 1 ? "s" : ""}
                      </p>
                      <button className={classes.currentProjection__toggleButton}>
                        <ChevronDown
                          size={20}
                          strokeWidth={1.4}
                          color="#0C0E29"
                          style={{
                            transform: revenueCollapsed ? "rotate(0deg)" : "rotate(-180deg)",
                            transition: "transform 0.3s ease",
                          }}
                        />
                      </button>
                    </div>
                  </div>
                  {!revenueCollapsed && (
                    <div className={classes.currentProjection__categoryUsage}>
                      <div className={classes.dividerLine} />
                      <Divider style={{ margin: "1rem 0" }} />
                      {currentProjection?.accounts?.filter((account) => account.isRevenue).length > 0 && (
                        <div className={classes.currentProjection__accounts}>
                          {currentProjection?.accounts
                            .filter((account) => account.isRevenue) // Filter companies with non-empty activeDateRanges
                            .sort((a, b) => a.name.localeCompare(b.name)) // Sort by name
                            .map((account) => {
                              return (
                                <div key={account.id} className={classes.accounts__accountContainer}>
                                  <p className={classes.accounts__accountName}>{account.name}</p>
                                  <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                    {didAccountChange(account.id) && (
                                      <Chip variant="warning" size="extraSmall">
                                        Modified
                                      </Chip>
                                    )}
                                    <Input
                                      type="number"
                                      value={account.percentage}
                                      style={{ width: "100px", color: account.percentage > 100 ? "#3AB037" : account.percentage == 100 ? "#0C0E29" : "#D84949" }}
                                      onChange={(e) => setAccountProjectionPercentage(account.id, e.target.value)}
                                      endAdornment={<Percent />}
                                      hideControls
                                      min={0}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {/* Costs */}
              {currentProjection?.accounts?.filter((account) => !account.isRevenue).length > 0 && (
                <div className={classes.currentProjection__category}>
                  <div className={classes.currentProjection__categoryHeader} onClick={toggleCostsCollapsed}>
                    <div className={classes.currentProjection__categoryTitleContainer}>
                      <Chip variant="danger">Costs</Chip>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <p className={classes.currentProjection__categoryCount}>
                        {currentProjection?.accounts?.filter((account) => !account.isRevenue).length} Simulation
                        {currentProjection?.accounts?.filter((account) => !account.isRevenue).length > 1 ? "s" : ""}
                      </p>
                      <button className={classes.currentProjection__toggleButton}>
                        <ChevronDown
                          size={20}
                          strokeWidth={1.4}
                          color="#0C0E29"
                          style={{
                            transform: costsCollapsed ? "rotate(0deg)" : "rotate(-180deg)",
                            transition: "transform 0.3s ease",
                          }}
                        />
                      </button>
                    </div>
                  </div>
                  {!costsCollapsed && (
                    <div className={classes.currentProjection__categoryUsage}>
                      <div className={classes.dividerLine} />
                      <Divider style={{ margin: "1rem 0" }} />
                      {currentProjection?.accounts?.filter((account) => !account.isRevenue).length > 0 && (
                        <div className={classes.currentProjection__accounts}>
                          {currentProjection?.accounts
                            .filter((account) => !account.isRevenue) // Filter companies with non-empty activeDateRanges
                            .sort((a, b) => a.name.localeCompare(b.name)) // Sort by name
                            .map((account) => {
                              return (
                                <div key={account.id} className={classes.accounts__accountContainer}>
                                  <p className={classes.accounts__accountName}>{account.name}</p>
                                  <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                    {didAccountChange(account.id) && (
                                      <Chip variant="warning" size="extraSmall">
                                        Modified
                                      </Chip>
                                    )}
                                    <Input
                                      type="number"
                                      value={account.percentage}
                                      style={{ width: "100px", color: account.percentage > 100 ? "#D84949" : account.percentage == 100 ? "#0C0E29" : "#3AB037" }}
                                      onChange={(e) => setAccountProjectionPercentage(account.id, e.target.value)}
                                      endAdornment={<Percent />}
                                      hideControls
                                      min={0}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {!currentProjection?.accounts?.length > 0 && (
              <div className={classes.emptyStateContainer}>
                <CircleOff className={classes.emptyState__icon} />
                <p className={classes.emptyState__text}>There are no accounts selected for projection</p>
              </div>
            )}
            {currentProjection?.accounts?.length > 0 && (
              <div style={{ display: "flex", flexDirection: "row", gap: ".5rem", marginTop: "1rem" }}>
                <Button onClick={handleResetProjection} variant={"outlined"} iconButton startIcon={<RotateCcw />} />
                <Button onClick={handleApplyProjection} fullWidth>
                  Apply projection
                </Button>
              </div>
            )}
          </div>
        )}
        {/* TODO: Events */}
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  button: {
    height: "32px",
    padding: "4px 8px 4px 12px",
    backgroundColor: "#DCDCDC",
    color: "#0C0E29",
    border: "1px solid #DCDCDC",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#BDBDBD",
    },
  },

  buttonOpen: {
    backgroundColor: "#FFFFFF", // Change background color to white when dropdown is open

    "&:hover": {
      backgroundColor: "#FFFFFF", // Keep the background color white on hover when open
    },
  },

  dropdown: {
    position: "absolute",
    top: "100%",
    left: "50%",
    transform: "translateX(-50%)",
    marginTop: "4px",
    width: "400px",
    maxWidth: "calc(90vw)",
    padding: "16px",
    backgroundColor: "#fff",
    border: "1px solid #DCDCDC",
    borderRadius: "8px",
    boxShadow: "0 4px 4px -4px rgba(12, 12, 13, 0.05), 0 6px 32px -4px rgba(12, 12, 13, 0.1)",
    zIndex: 9998,
    opacity: 0,
    transformOrigin: "top center",
    transition: "opacity 0.3s ease, transform 0.3s ease",
  },

  dropdownOpen: {
    opacity: 1,
    transform: "translateX(-50%)",
    pointerEvents: "auto",
  },

  dropdownClosed: {
    opacity: 0,
    transform: "translateX(-50%)",
    pointerEvents: "none",
  },

  // SelectTrigger-like input styles
  selectTriggerInput: {
    padding: "8px 12px",
    border: "1px solid #DCDCDC",
    borderRadius: "4px",
    backgroundColor: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    transition: "border-color 0.2s",
    "&:hover": {
      borderColor: "#888",
    },
  },

  selectTriggerInputError: {
    borderColor: "#D84949 !important",
    "&:hover": {
      borderColor: "#D84949 !important",
    },
  },

  selectTriggerInputField: {
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    flex: 1,
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    cursor: "text",
    color: "#0C0E29",
    "&::placeholder": {
      color: "rgba(12, 14, 41, 0.6)",
    },
  },

  selectTriggerChevronButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "2px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transform: "translateX(2px)",
    "&:hover": {
      backgroundColor: "#F0F0F0",
    },
  },

  selectTriggerIcon: {
    width: "16px",
    height: "16px",
    transition: "transform 0.2s",
    cursor: "pointer",
    color: "#666",
  },

  selectTriggerIconOpen: {
    transform: "rotate(180deg)",
  },

  dropdownContainer: {
    width: "100%",
    minWidth: "160px",
    maxHeight: "200px",
    overflowY: "auto",
    position: "absolute",
    zIndex: 9998,
    backgroundColor: "#FFFFFF",
    borderRadius: "4px",
    border: "1px solid #DCDCDC",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",

    display: "flex",
    flexDirection: "column",

    padding: "4px",

    "&::-webkit-scrollbar": {
      width: "4px",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },

  dropdown__menuItem: {
    width: "100%",
    minHeight: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",
    backgroundColor: "#FFFFFF",

    "&:hover": {
      backgroundColor: "#F4F4F4",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },

  dropdown__emptyState: {
    height: "fit-content",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",

    padding: "24px 0 16px 0",
  },

  emptyStateContainer: {
    height: "fit-content",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",

    padding: "24px 0 16px 0",
    marginTop: "16px",
  },

  emptyState__icon: {
    width: "24px",
    height: "24px",
    color: "#0C0E29",

    strokeWidth: "1.4px",
  },

  emptyState__text: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
    margin: "0",
  },

  currentProjectionContainer: {
    width: "100%",

    display: "flex",
    flexDirection: "column",
    gap: "8px",

    marginTop: "16px",
  },

  currentProjection__category: {
    width: "100%",

    padding: "16px",
    border: "1px solid #DCDCDC",
    borderRadius: "8px",
  },

  currentProjection__categoryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  currentProjection__categoryTitleContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  currentProjection__categoryTitle: {
    height: "24px",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",

    borderRadius: "4px",

    padding: "0px 12px",
    verticalAlign: "middle",
  },

  currentProjection__categoryCount: {
    fontSize: "18px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
  },

  currentProjection__toggleButton: {
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "0px",
    margin: "0px",
    height: "24px",
    width: "24px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "4px",
    transition: "all ease-in-out 0.2s",
    "&:hover": {
      backgroundColor: "#DCDCDC",
      cursor: "pointer",
    },
    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  currentProjection__emptyStateContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  currentProjection__accounts: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: ".5rem",

    // max height of 200px and scrollable
    maxHeight: "12.5rem",
    overflowY: "auto",
    overflowX: "hidden",

    // custom scrollbar
    "&::-webkit-scrollbar": {
      width: "4px",
      paddingLeft: "2px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },

  accounts__accountContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
  },

  accounts__accountName: {
    width: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
  },

  // mobile alignment
  leftMobileAlignment: {
    left: "0",
    transform: "translateX(0)",
  },
  centerMobileAlignment: {
    left: "50%",
    transform: "translateX(-50%)",
  },
  rightMobileAlignment: {
    left: "auto",
    right: "0",
    transform: "translateX(0)",
  },
});

export default ProjectionsDialog;
